<template>
    <div v-if="isVerificationEditor">
        <div v-if="errorMessage" class="alert alert-danger"> {{ errorMessage }}</div>
        <div v-if="!isBusy && (!model || model?.companiesHouse === null)" class="alert alert-warning">
            No Companies House data available.
        </div>
        <card v-if="model && model.companiesHouse" label="Company Details">
            <div class="row">
                <div class="col-6">
                    <div class="form-label">
                        <key-value-presenter label="Registered Company Number"
                            :value="model.companiesHouse.company?.company_number" />
                    </div>
                </div>
                <div class="col-6">
                    <div class="form-label">
                        <key-value-presenter label="Previous Names" :value="formattedPreviousNames" />
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-6">
                    <div class="form-label">
                        <key-value-presenter label="Registered Address"
                            :value="formattedAddress(model.companiesHouse.company?.registered_office_address)" />
                    </div>
                </div>
                <div class="col-6">
                    <div class="form-label d-flex justify-content-between w-50">
                        <key-value-presenter label="Formed"
                            :value="formattedDate(model.companiesHouse.company?.date_of_creation)" />
                        <key-value-presenter label="Ceased"
                            :value="formattedDate(model.companiesHouse.company?.date_of_cessation)" />
                    </div>
                    <div class="form-label">
                        <key-value-presenter label="Company Type"
                            :value="model.companiesHouse.company?.type ?? 'N/A'" />
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-6">
                    <div class="form-label">
                        <key-value-presenter label="Status"
                            :value="capitalizeString(model.companiesHouse.company?.company_status)" />
                    </div>

                </div>
                <div class="col-6">
                    <div class="form-label d-flex justify-content-between w-50">
                        <key-value-presenter label="Statement"
                            :value="formattedDate(model.companiesHouse.company?.confirmation_statement?.last_made_up_to)" />
                        <key-value-presenter label="Overdue?"
                            :value="model.companiesHouse.company?.confirmation_statement?.overdue ? 'True' : 'False'" />
                    </div>
                    <div class="form-label">
                        <key-value-presenter label="Last Accounts"
                            :value="$helpers.dateString(model.companiesHouse.company?.accounts?.last_accounts?.period_end_on)" />
                    </div>
                </div>
            </div>
        </card>

        <Card v-if="model && model.companiesHouse && model.companiesHouse?.officers?.items.length > 0" label="Officers">
            <table class="table table-hover">
                <thead>
                    <tr>
                        <th scope="col">PersonId</th>
                        <th scope="col">Name</th>
                        <th scope="col">Role</th>
                        <th scope="col">Appointed</th>
                        <th scope="col">Resigned</th>
                        <th scope="col">DoB</th>
                        <th scope="col">Address</th>
                        <th scope="col">Occupation</th>
                        <th scope="col">Appointments >></th>
                    </tr>
                </thead>
                <tbody v-for="officer in model.companiesHouse?.officers?.items" :key="officer.person_number">
                    <tr>
                        <td>{{ officer.person_number }}</td>
                        <td>{{ officer.name }}</td>
                        <td>{{ capitalizeString(officer.officer_role) }}
                            <span :class="officer.resigned_on ? 'bg-secondary' : 'bg-success'" class="badge">
                                {{ officer.resigned_on ? 'Resigned' : 'Active' }}</span>
                        </td>
                        <td>{{ formattedDate(officer.appointed_on) }}</td>
                        <td>{{ formattedDate(officer.resigned_on) }}</td>
                        <td>{{ formattedDateOfBirth(officer.date_of_birth) }}</td>
                        <td>{{ formattedAddress(officer.address) }}</td>
                        <td>{{ officer.occupation }}</td>
                        <td :class="{ 'no-border-bottom': isPanelExpanded[officer?.person_number] }" class="text-right">
                            <button class="btn btn-primary" @click="toggleEdit(officer?.person_number)">
                                <span v-if="isPanelExpanded[officer.person_number]">
                                    <font-awesome-icon icon="chevron-down" />
                                </span>
                                <span v-else>
                                    <font-awesome-icon icon="chevron-up" />
                                </span>
                            </button>
                        </td>
                    </tr>
                    <!-- Expandable Appointment row -->
                    <tr v-if="isPanelExpanded[officer?.person_number]" class="bg-light">
                        <td colspan="6" class="p-3">
                            <card :label="`Total number of appointments ${officer?.appointments?.total_results}`">
                                <div class="row">
                                    <div class="col form-label">
                                        <key-value-presenter label="Date of Birth"
                                            :value="formattedDateOfBirth(officer.date_of_birth)" />
                                    </div>
                                </div>
                                <div v-for="(appointment, index) in officer?.appointments?.items" :key="index">
                                    <h6 class="py-3">{{ appointment.appointed_to.company_name }}</h6>
                                    <div class="row ">
                                        <div class="col-4 form-label">
                                            <key-value-presenter label="Company Status"
                                                :value="capitalizeString(appointment.appointed_to.company_status)" />
                                        </div>
                                        <div class="col-8 form-label">
                                            <key-value-presenter label="Correspondence Address"
                                                :value="formattedAddress(appointment.address)" />
                                        </div>
                                    </div>
                                    <div class="row ">
                                        <div class="col-4 form-label d-flex align-items-center">
                                            <key-value-presenter label="Role"
                                                :value="capitalizeString(appointment.officer_role)" />
                                            <span
                                                v-if="appointment?.appointed_to?.company_status.toLowerCase() !== 'dissolved'"
                                                :class="appointment.resigned_on ? 'bg-secondary' : 'bg-success'"
                                                class="badge ms-2">
                                                {{ appointment.resigned_on ? 'Resigned' : 'Active' }}</span>
                                        </div>
                                        <div class="col-4 form-label">
                                            <key-value-presenter label="Appointed on"
                                                :value="formattedDate(appointment.appointed_on)" />
                                        </div>
                                        <div v-if="appointment.resigned_on" class="col-4 form-label">
                                            <key-value-presenter label="Resigned on"
                                                :value="formattedDate(appointment.resigned_on)" />
                                        </div>
                                    </div>
                                    <div class="row ">
                                        <div class="col-4 form-label">
                                            <key-value-presenter label="Nationality"
                                                :value="appointment.nationality ?? 'N/A'" />
                                        </div>
                                        <div class="col-4 form-label">
                                            <key-value-presenter label="Country of Residence"
                                                :value="appointment.country_of_residence ?? 'N/A'" />
                                        </div>
                                        <div class="col-4 form-label">
                                            <key-value-presenter label="Occupation"
                                                :value="appointment.occupation ?? 'N/A'" />
                                        </div>
                                    </div>
                                    <hr class="border border-dark w-100">
                                </div>
                            </card>
                        </td>
                    </tr>
                </tbody>
            </table>
        </Card>

        <Card v-if="model && model.companiesHouse && model.companiesHouse?.officers?.items.length > 0"
            label="PSC Details">
            <table class="table table-hover">
                <thead>
                    <tr>
                        <th scope="col">Name</th>
                        <th scope="col">DoB</th>
                        <th scope="col">Notified</th>
                        <th scope="col">Ceased</th>
                        <th scope="col">Nature of Control</th>
                        <th scope="col">Address</th>
                    </tr>
                </thead>
                <tbody v-for="officer in model.companiesHouse?.officers?.items" :key="officer.person_number">
                    <tr v-if="officer.kind && officer.kind === 'individual-person-with-significant-control'">
                        <td>{{ officer.name }}</td>
                        <td>{{ formattedDateOfBirth(officer.date_of_birth) }}</td>
                        <td>{{ formattedDate(officer.appointed_on) }}</td>
                        <td>{{ formattedDate(officer.ceased_on) }}</td>
                        <td v-for="(nc, index) in formattedNatureOfControl(officer.natures_of_control)" :key="index">
                            {{ nc }}</td>
                        <td>{{ formattedAddress(officer.address) }}</td>
                    </tr>
                </tbody>
            </table>
        </Card>

        <Card v-if="model && model.companiesHouse && model.companiesHouse.filingHistory" label="Filing History">
            <table class="table table-hover">
                <thead>
                    <tr>
                        <th scope="col">Date</th>
                        <th scope="col">Category</th>
                        <th scope="col">Description</th>
                        <th scope="col">Link</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="fh in model.companiesHouse.filingHistory.items" :key="fh.transaction_id">
                        <td>{{ formattedDate(fh.date) }}</td>
                        <td>{{ capitalizeString(fh.category) }}</td>
                        <td>{{ formattedDescription(fh) }}</td>
                        <td>
                            <button type="button" class="btn btn-secondary"
                                @click="downloadFile(fh.links.document_metadata)">Download File</button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </Card>
    </div>
</template>

<script>
import { Card, KeyValuePresenter } from '@trustmark-2005-ltd/trustmark-shared-components';

export default {
    name: "SchemeBusinessVerificationCompaniesHouse",
    components: {
        Card,
        KeyValuePresenter
    },
    data() {
        return {
            errorMessage: null,
            isPanelExpanded: {},
        };
    },
    computed: {
        currentAccount() {
            return this.$store.state.auth.account;
        },
        isVerificationEditor() {
            return this.currentAccount && this.currentAccount.roles
                ? this.currentAccount.roles.includes('VerificationEditor')
                : false;
        },
        model() {
            return this.$store.state.schemeBusiness.verification;
        },
        formattedPreviousNames() {
            const previousNames = this.model?.companiesHouse?.company?.previous_company_names;
            if (!previousNames || previousNames.length == 0) {
                return 'N/A';
            }
            return previousNames.map(n => n.name).join(', ');
        },
        isBusy() {
            return this.$store.state.isBusy;
        },
    },
    async mounted() {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        if (!this.model) {
            await this.$store.dispatch('schemeBusiness/getVerification', { schemeBusinessId: this.$route.params.schemeBusinessId, schemeId: this.$route.params.schemeId });
        }
    },
    methods: {
        formattedAddress(address) {
            // check for nulls on each property
            var formattedAddress = '';
            if (address) {
                if (address.address_line_1) {
                    formattedAddress += address.address_line_1 + ', ';
                }
                if (address.address_line_2) {
                    formattedAddress += address.address_line_2 + ', ';
                }
                if (address.locality) {
                    formattedAddress += address.locality + ', ';
                }
                if (address.city) {
                    formattedAddress += address.city + ', ';
                }
                if (address.county) {
                    formattedAddress += address.county + ', ';
                }
                if (address.postcode) {
                    formattedAddress += address.postcode + ', ';
                }
                if (address.country) {
                    formattedAddress += address.country + ', ';
                }
            }
            // cut off the last comma
            if (formattedAddress.endsWith(', ')) {
                formattedAddress = formattedAddress.slice(0, -2);
            }
            return formattedAddress;
        },
        capitalizeString(str) {
            return str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : 'N/A';
        },
        formattedDate(date) {
            return date ? this.$helpers.dateString(date) : 'N/A';
        },
        formattedDateOfBirth(dateOfBirth) {
            const { month, year } = dateOfBirth;
            switch (month) {
                case 1:
                    return `January ${year}`;
                case 2:
                    return `February ${year}`;
                case 3:
                    return `March ${year}`;
                case 4:
                    return `April ${year}`;
                case 5:
                    return `May ${year}`;
                case 6:
                    return `June ${year}`;
                case 7:
                    return `July ${year}`;
                case 8:
                    return `August ${year}`;
                case 9:
                    return `September ${year}`;
                case 10:
                    return `October ${year}`;
                case 11:
                    return `November ${year}`;
                case 12:
                    return `December ${year}`;
                default:
                    return 'N/A';
            }
        },
        toggleEdit(personNumber) {
            this.isPanelExpanded[personNumber] = !this.isPanelExpanded[personNumber];
        },
        formattedNatureOfControl(controlsArray) {
            if (!controlsArray || controlsArray.length === 0) return 'N/A';
            let result = [];

            controlsArray.forEach(element => {
                const wordsArray = element.split('-');
                let str = '';

                wordsArray.forEach((word, index) => {
                    if (index === 0) {
                        str += this.capitalizeString(word);
                    }
                    str += (index > 0 ? ' ' : ' ') + word;
                });
                result.push(str);
            });
            return result;
        },
        formattedDescription(filingHistory) {
            if (!filingHistory) return 'N/A';
            const { category, type, description, description_values } = filingHistory;

            if (category === 'gazette') {
                if (type === 'GAZ1' && description.includes('notice')) {
                    return `First ${this.capitalizeString(category)} notice for compulsory strike-off`
                }
                if (type === 'GAZ2' && description.includes('dissolved')) {
                    return `Final ${this.capitalizeString(category)} dissolved via compulsory strike-off`
                }
            }
            if (category === 'accounts' && type === 'AA' && description.includes('dormant') && description_values) {
                return `${this.capitalizeString(category)} for a dormant company made up to ${this.formattedDate(description_values.made_up_date)}`
            }
            if (category === 'confirmation-statement' && type === 'CS01' && description.includes('no-updates')) {
                return `${this.capitalizeString(category)} made on ${this.formattedDate(description_values.made_up_date)} with no updates`
            }
            if (category === 'officers' && filingHistory.subcategory === 'appointments' && description_values) {
                if (type === 'AP01' && description.includes('director') && description.includes('name-date')) {
                    return `Appointment of ${description_values.officer_name} as a director on ${this.formattedDate(description_values.appointment_date)}`
                }
            }
            if (category === 'incorporation' && type === 'NEWINC' && description.includes('incorporation')) {
                return `${this.capitalizeString(category)} - Model articles adopted`
            }

            return 'N/A';
        },
        getDocumentId(documentMetadata) {
            if (typeof documentMetadata !== 'string' || !documentMetadata.trim()) {
                return null;
            }
            return documentMetadata.split('/').pop();
        },
        downloadFile(documentMetadata) {
            const documentId = this.getDocumentId(documentMetadata)
            if (!documentId) {
                this.errorMessage = 'Invalid document ID. Please try again later';
                window.scrollTo(0, 0);
                return;
            }

            this.$store.dispatch('schemeBusiness/getFilingHouseFile', documentId)
                .then((result) => {
                    const blob = new Blob([result], { type: 'application/pdf' });
                    const url = window.URL.createObjectURL(blob);
                    window.open(url, '_blank');
                })
                .catch(() => {
                    this.errorMessage = 'Could not download file, please try again';
                    window.scrollTo(0, 0);
                });
        },
    }
}
</script>
